/**
 * Company Dashboard Page
 *
 * Main dashboard for company users showing overview of:
 * - Application status and progress
 * - Document vault summary
 * - Supplier information
 * - Recent activity timeline
 * - Quick action buttons
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FileText, Folder, Factory, Plus, ClipboardList } from 'lucide-react';
import { PageLayout } from '../../components/layouts/PageLayout';
import { Card, CardHeader, CardBody } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Badge } from '../../components/common/Badge';
import { ProgressBar } from '../../components/common/ProgressBar';
import { StatCard } from '../../components/dashboard/StatCard';
import { DashboardGrid } from '../../components/dashboard/DashboardGrid';
import { RecentActivityCard } from '../../components/dashboard/RecentActivityCard';
import { QuickActionsPanel } from '../../components/dashboard/QuickActionsPanel';
import type { ActivityItem as DashboardActivityItem } from '../../components/dashboard/RecentActivityCard';
import { mockApi } from '../../services/mockApi';
import type { Application } from '../../types/application';

interface DashboardStats {
  applications: {
    total: number;
    inProgress: number;
    approved: number;
    draft: number;
  };
  documents: {
    total: number;
    expiringSoon: number;
    unverified: number;
  };
  suppliers: {
    total: number;
    certificatesExpiring: number;
  };
}

interface ActivityItem {
  id: string;
  type: 'application' | 'document' | 'supplier';
  action: string;
  timestamp: Date;
  description: string;
}

export const CompanyDashboardPage: React.FC = () => {
  const { t } = useTranslation(['common', 'company']);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats>({
    applications: { total: 0, inProgress: 0, approved: 0, draft: 0 },
    documents: { total: 0, expiringSoon: 0, unverified: 0 },
    suppliers: { total: 0, certificatesExpiring: 0 },
  });
  const [recentApplications, setRecentApplications] = useState<Application[]>([]);
  const [recentActivity, setRecentActivity] = useState<ActivityItem[]>([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch data from mock API
      const [applicationsRes, documentsRes, suppliersRes, expiringDocsRes] = await Promise.all([
        mockApi.getCompanyApplications('org-001'), // Mock company ID
        mockApi.getCompanyDocuments('org-001'),
        mockApi.getAllSuppliers(),
        mockApi.getExpiringDocuments(30),
      ]);

      // Calculate application stats
      const applications = applicationsRes.data || [];
      const appStats = {
        total: applications.length,
        inProgress: applications.filter((a) =>
          ['SUBMITTED', 'UNDER_REVIEW', 'CONSULTANT_ASSIGNED', 'PRE_AUDIT_SCHEDULED', 'AUDIT_IN_PROGRESS'].includes(a.status)
        ).length,
        approved: applications.filter((a) => a.status === 'APPROVED').length,
        draft: applications.filter((a) => a.status === 'DRAFT').length,
      };

      // Calculate document stats
      const documents = documentsRes.data || [];
      const docStats = {
        total: documents.length,
        expiringSoon: (expiringDocsRes.data || []).length,
        unverified: documents.filter((d) => !d.verified).length,
      };

      // Calculate supplier stats (mock)
      const suppliers = suppliersRes.data || [];
      const supplierStats = {
        total: suppliers.length,
        certificatesExpiring: suppliers.filter((s) => {
          if (!s.halalCertified || !s.certificationExpiryDate) return false;
          const daysUntilExpiry = Math.ceil(
            (new Date(s.certificationExpiryDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)
          );
          return daysUntilExpiry <= 60 && daysUntilExpiry > 0;
        }).length,
      };

      setStats({
        applications: appStats,
        documents: docStats,
        suppliers: supplierStats,
      });

      // Set recent applications (top 3)
      setRecentApplications(applications.slice(0, 3));

      // Generate recent activity (mock)
      const activity: ActivityItem[] = [
        {
          id: '1',
          type: 'application',
          action: 'submitted',
          timestamp: new Date('2025-01-16T16:00:00'),
          description: 'Application APP-2025-0001 submitted for review',
        },
        {
          id: '2',
          type: 'document',
          action: 'uploaded',
          timestamp: new Date('2025-01-15T11:00:00'),
          description: 'Halal Policy document uploaded',
        },
        {
          id: '3',
          type: 'application',
          action: 'updated',
          timestamp: new Date('2025-01-20T10:00:00'),
          description: 'Pre-audit scheduled for APP-2025-0001',
        },
        {
          id: '4',
          type: 'supplier',
          action: 'added',
          timestamp: new Date('2025-01-10T14:00:00'),
          description: 'New supplier "Halal Spices Co" added',
        },
        {
          id: '5',
          type: 'document',
          action: 'verified',
          timestamp: new Date('2025-01-17T09:30:00'),
          description: 'Business License verified by CB',
        },
      ];
      setRecentActivity(activity.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime()));
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getApplicationStatusBadge = (status: string) => {
    const statusMap: Record<string, { variant: 'default' | 'success' | 'warning' | 'error' | 'info'; label: string }> = {
      DRAFT: { variant: 'default', label: t('common:status.draft') },
      SUBMITTED: { variant: 'info', label: t('common:status.submitted') },
      APPROVED: { variant: 'success', label: t('common:status.approved') },
      UNDER_REVIEW: { variant: 'warning', label: 'Under Review' },
      AUDIT_IN_PROGRESS: { variant: 'warning', label: 'Audit In Progress' },
    };
    const config = statusMap[status] || { variant: 'default' as const, label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <PageLayout pageTitle={t('company:dashboard.title')}>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">{t('common:status.loading')}...</div>
        </div>
      </PageLayout>
    );
  }

  // Convert activity data to new format
  const activityItems: DashboardActivityItem[] = recentActivity.map((activity) => {
    let icon = <FileText className="w-4 h-4" />;
    let variant: 'default' | 'primary' | 'success' | 'warning' | 'error' = 'default';

    if (activity.type === 'application') {
      icon = <ClipboardList className="w-4 h-4" />;
      variant = 'primary';
    } else if (activity.type === 'document') {
      icon = <Folder className="w-4 h-4" />;
      variant = 'success';
    } else if (activity.type === 'supplier') {
      icon = <Factory className="w-4 h-4" />;
      variant = 'warning';
    }

    return {
      id: activity.id,
      icon,
      title: activity.action.charAt(0).toUpperCase() + activity.action.slice(1),
      description: activity.description,
      timestamp: activity.timestamp,
      variant,
    };
  });

  return (
    <PageLayout
      pageTitle={t('company:dashboard.title')}
      footer={{ copyright: "© 2026 HALMAS. Araken Sdn. Bhd. All rights reserved." }}
      breadcrumbs={[
        { label: t('common:navigation.home'), href: '/' },
        { label: t('company:dashboard.title') },
      ]}
    >
      {/* Quick Actions */}
      <QuickActionsPanel
        className="mb-6"
        actions={[
          {
            id: 'new-app',
            label: t('company:applications.newApplication'),
            icon: <Plus className="w-4 h-4" />,
            onClick: () => navigate('/company/applications'),
            variant: 'primary',
          },
          {
            id: 'upload-doc',
            label: t('company:documents.uploadDocument'),
            icon: <Folder className="w-4 h-4" />,
            onClick: () => navigate('/company/documents'),
            variant: 'secondary',
          },
          {
            id: 'add-supplier',
            label: t('company:suppliers.addSupplier'),
            icon: <Factory className="w-4 h-4" />,
            onClick: () => navigate('/company/suppliers'),
            variant: 'outline',
          },
        ]}
      />

      {/* Dashboard Stats Cards */}
      <DashboardGrid cols={3} className="mb-8">
        <StatCard
          title={t('company:dashboard.applications')}
          value={stats.applications.total}
          icon={<ClipboardList className="w-6 h-6" />}
          variant="primary"
          subtitle={`${stats.applications.inProgress} in progress, ${stats.applications.approved} approved`}
          action={{
            label: t('common:actions.viewAll'),
            onClick: () => navigate('/company/applications'),
          }}
        >
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-neutral-600">{t('common:status.draft')}</span>
              <span className="font-medium">{stats.applications.draft}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600">In Progress</span>
              <span className="font-medium text-warning-600">{stats.applications.inProgress}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-600">{t('common:status.approved')}</span>
              <span className="font-medium text-success-600">{stats.applications.approved}</span>
            </div>
          </div>
        </StatCard>

        <StatCard
          title={t('common:navigation.documents')}
          value={stats.documents.total}
          icon={<Folder className="w-6 h-6" />}
          variant={stats.documents.expiringSoon > 0 ? 'warning' : 'default'}
          subtitle={
            stats.documents.expiringSoon > 0
              ? `${stats.documents.expiringSoon} expiring soon`
              : stats.documents.unverified > 0
                ? `${stats.documents.unverified} pending verification`
                : 'All documents up to date'
          }
          action={{
            label: t('common:actions.manage'),
            onClick: () => navigate('/company/documents'),
          }}
        />

        <StatCard
          title={t('company:suppliers.title')}
          value={stats.suppliers.total}
          icon={<Factory className="w-6 h-6" />}
          variant={stats.suppliers.certificatesExpiring > 0 ? 'warning' : 'success'}
          subtitle={
            stats.suppliers.certificatesExpiring > 0
              ? `${stats.suppliers.certificatesExpiring} certificates expiring soon`
              : 'All suppliers certified'
          }
          action={{
            label: t('common:actions.manage'),
            onClick: () => navigate('/company/suppliers'),
          }}
        />
      </DashboardGrid>

      {/* Recent Applications & Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Applications */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">{t('company:dashboard.recentApplications')}</h3>
              <Button variant="outline" size="sm" onClick={() => navigate('/company/applications')}>
                {t('common:actions.view')} {t('common:common.all')}
              </Button>
            </div>
          </CardHeader>
          <CardBody>
            <div className="space-y-3">
              {recentApplications.length === 0 ? (
                <p className="text-gray-500 text-center py-4">{t('company:applications.noApplications')}</p>
              ) : (
                recentApplications.map((app) => (
                  <div
                    key={app.id}
                    className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/company/applications/${app.id}`)}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="font-medium text-blue-600">{app.applicationNumber}</div>
                        <div className="text-sm text-gray-600">{app.type.replace(/_/g, ' ')}</div>
                      </div>
                      {getApplicationStatusBadge(app.status)}
                    </div>
                    {app.completenessScore !== undefined && (
                      <div className="mt-2">
                        <div className="flex justify-between text-xs text-gray-600 mb-1">
                          <span>Completeness</span>
                          <span>{app.completenessScore}%</span>
                        </div>
                        <ProgressBar
                          value={app.completenessScore}
                          variant={app.completenessScore === 100 ? 'success' : 'default'}
                          size="sm"
                          showLabel={false}
                        />
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </CardBody>
        </Card>

        {/* Recent Activity */}
        <RecentActivityCard
          title={t('company:dashboard.recentActivity')}
          items={activityItems}
          maxItems={5}
          showViewAll
          onViewAllClick={() => navigate('/company/applications')}
        />
      </div>
    </PageLayout>
  );
};

export default CompanyDashboardPage;
